/**
 * Helpers de calcul de garde — Phase 1, incrément 13.
 *
 * Port de `getCustodyForDate` du proto (l. 1285-1294) en module typé.
 * Réutilisé par les vues co-parentage (CpHome, CpActivities, CpLiaison)
 * pour afficher « Avec X aujourd'hui ».
 *
 * Pour `weekly` : alterne tous les 7 jours à partir de `startDate`.
 * Le `weekParity` détermine si la semaine de `startDate` est chez A
 * (parity=0) ou chez B (parity=1).
 *
 * Les autres patterns (`2-2-3`, `2-2-5-5`, `weekends_alternating`,
 * `custom`) sont reportés à la Phase 6 (cf. roadmap §6.3) — pour
 * l'instant on tombe sur `custody.parentA` par défaut.
 */
import type { CustodyConfig } from '@nestor/shared-types';

const DAY_MS = 86_400_000;

/**
 * Retourne le **nom du parent référent** (string) pour une date donnée.
 * `null` si la garde n'est pas configurée.
 */
export function getCustodyForDate(
  date: Date,
  custody: CustodyConfig | null | undefined,
): string | null {
  if (!custody?.startDate) return null;
  const start = new Date(custody.startDate);
  const diffDays = Math.floor((date.getTime() - start.getTime()) / DAY_MS);
  if (custody.type === 'weekly') {
    const isEven = Math.floor(diffDays / 7) % 2 === (custody.weekParity ?? 0);
    return isEven ? custody.parentA : custody.parentB;
  }
  // Patterns Phase 6 — fallback sur parentA en attendant.
  return custody.parentA;
}

/**
 * Variante typée qui retourne `'A' | 'B' | null` (utile pour matcher
 * `CpActivity.parent` qui utilise ce code court).
 */
export function getCustodyParentCode(
  date: Date,
  custody: CustodyConfig | null | undefined,
): 'A' | 'B' | null {
  if (!custody?.startDate) return null;
  const name = getCustodyForDate(date, custody);
  if (name === custody?.parentA) return 'A';
  if (name === custody?.parentB) return 'B';
  return null;
}

/**
 * Résout un code parent (`'A' | 'B'`) en nom à partir de la config de
 * garde. Utile pour afficher le badge d'une activité.
 */
export function parentNameByCode(
  code: 'A' | 'B' | undefined,
  custody: CustodyConfig | null | undefined,
): string {
  if (!custody) return '';
  if (code === 'A') return custody.parentA;
  if (code === 'B') return custody.parentB;
  return '';
}
